<template>
  <div class="stall-after-sales-detail">
    <div class="page-header">
      <div class="back-btn" @click="goBack">← {{ $t('common.back') }} / Back</div>
      <h1>{{ $t('afterSales.title') }}{{ $t('afterSales.detail') }}</h1>
      <div class="sub">{{ detail.afterSalesNo || '' }}</div>
    </div>

    <div v-loading="loading" class="content">
      <!-- 进度条 -->
      <div class="module">
        <div class="module-header">
          <span class="icon">📊</span><span class="title">{{ $t('afterSales.progress') }}</span>
        </div>
        <div class="module-body">
          <div class="progress-bar">
            <div class="progress-step" v-for="(step, i) in steps" :key="i">
              <div class="progress-dot" :class="stepClass(i)">{{ step.icon }}</div>
              <div class="progress-label" :class="{ active: currentStep >= i }">{{ step.label }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 售后信息 -->
      <div class="module">
        <div class="module-header"><span class="icon">📋</span><span class="title">{{ $t('afterSales.afterSalesInfo') }}</span></div>
        <div class="module-body">
          <div class="row"><span class="label">{{ $t('afterSales.afterSalesNo') }}</span><span class="value mono">{{ detail.afterSalesNo }}</span></div>
          <div class="row"><span class="label">{{ $t('afterSales.relatedOrder') }}</span><span class="value mono" @click="goOrder" style="color:#457b9d;cursor:pointer;">{{ detail.orderNo }}</span></div>
          <div class="row"><span class="label">{{ $t('afterSales.problemReason') }}</span><span class="value">{{ reasonText(detail.reasonCode) }}</span></div>
          <div class="row"><span class="label">{{ $t('afterSales.problemDesc') }}</span><span class="value" style="text-align:right;">{{ detail.reasonText }}</span></div>
          <div class="row"><span class="label">{{ $t('afterSales.resolution') }}</span><span class="value" style="color:#1890ff;font-weight:500;">{{ schemeText(detail.resolutionType) }}</span></div>
          <div class="row"><span class="label">{{ $t('afterSales.returnMethod') }}</span><span class="value">{{ returnMethodText(detail.returnMethod) }}</span></div>
          <div class="row"><span class="label">{{ $t('afterSales.applyTime') }}</span><span class="value">{{ detail.requestedAt }}</span></div>
          <div class="row" v-if="detail.reviewedAt"><span class="label">{{ $t('afterSales.reviewTime') }}</span><span class="value">{{ detail.reviewedAt }}</span></div>
        </div>
      </div>

      <!-- 退款明细 -->
      <div v-if="detail.refundAmount > 0 || detail.gstRefundAmount > 0" class="module">
        <div class="module-header"><span class="icon">💰</span><span class="title">{{ $t('afterSales.refundDetails') }}</span></div>
        <div class="module-body">
          <div class="row"><span class="label">{{ $t('afterSales.productRefund') }}</span><span class="value">S$ {{ centsToAmount(detail.itemRefundAmount) }}</span></div>
          <div class="row"><span class="label">{{ $t('afterSales.gstRefund') }} ({{ gstRate }}%)</span><span class="value">S$ {{ centsToAmount(detail.gstRefundAmount) }}</span></div>
          <div class="row" style="padding-top:12px;border-top:2px solid #f0f0f0;"><span class="label" style="font-weight:600;">{{ $t('afterSales.totalRefund') }}</span><span class="value amount">S$ {{ centsToAmount(detail.refundAmount) }}</span></div>
        </div>
      </div>

      <!-- 操作日志 -->
      <div class="module">
        <div class="module-header"><span class="icon">📝</span><span class="title">{{ $t('common.action') }}记录 / Logs</span></div>
        <div class="module-body">
          <div v-if="actions.length > 0">
            <div class="log-item" v-for="log in actions" :key="log.id">
              <div class="log-dot"></div>
              <div class="log-content">
                <div class="log-title">{{ log.actionType }}</div>
                <div class="log-note" v-if="log.note">{{ log.note }}</div>
                <div class="log-time">{{ log.createdAt }}</div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state"><div class="empty-icon">📄</div><div class="empty-text">{{ $t('afterSales.noLog') }}</div></div>
        </div>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div class="bottom-actions">
      <el-button @click="goBack" style="flex:1;">{{ $t('afterSales.backToList') }}</el-button>
      <el-button type="primary" @click="printReceipt" style="flex:2;">🖨️ {{ $t('afterSales.printReceipt') }}</el-button>
    </div>
  </div>
</template>

<script>
import { getAfterSalesDetail, getAfterSalesActions, getRefundReceipt } from '@/services/afterSales';

export default {
  name: 'StallAfterSalesDetail',
  data() {
    return {
      loading: false,
      detail: {},
      actions: [],
      currentStep: 0,
      steps: [
        { icon: '1', label: '' },
        { icon: '2', label: '' },
        { icon: '3', label: '' },
        { icon: '4', label: '' },
        { icon: '5', label: '' },
        { icon: '6', label: '' }
      ]
    };
  },
  computed: {
    gstRate() {
      // 从订单或售后单中获取税率
      return 9;
    }
  },
  created() {
    this.loadDetail();
  },
  methods: {
    async loadDetail() {
      this.loading = true;
      try {
        const id = this.$route.params.id;
        this.detail = await getAfterSalesDetail(id);
        this.actions = await getAfterSalesActions(id);
        this.calcStep();
      } catch (e) {
        this.$message.error(this.$t('afterSales.loadFailed') + ': ' + (e.message || e));
      } finally {
        this.loading = false;
      }
    },
    calcStep() {
      const status = this.detail.status;
      const rs = this.detail.resolutionStatus;
      if (status === 'COMPLETED') this.currentStep = 5;
      else if (status === 'REVIEWED') {
        if (['REFUND_PROCESSING', 'REFUND_COMPLETED'].includes(rs)) this.currentStep = 4;
        else if (['INSPECTING', 'INSPECTION_PASSED', 'INSPECTION_FAILED'].includes(rs)) this.currentStep = 3;
        else if (['PICKING_UP', 'RETURN_RECEIVED'].includes(rs)) this.currentStep = 2;
        else this.currentStep = 1;
      } else if (status === 'REJECTED') this.currentStep = -1;
      else this.currentStep = 0;
    },
    stepClass(i) {
      if (this.currentStep < 0) return '';
      if (i < this.currentStep) return 'done';
      if (i === this.currentStep) return 'active';
      return '';
    },
    reasonText(code) {
      const map = { DAMAGED: this.$t('afterSales.reasonDamaged'), WRONG_ITEM: this.$t('afterSales.reasonWrongItem'), NOT_RECEIVED: this.$t('afterSales.reasonNotReceived'), WRONG_SPEC: this.$t('afterSales.reasonWrongSpec'), SHORT_DELIVERY: this.$t('afterSales.reasonShortDelivery'), DONT_WANT: this.$t('afterSales.reasonDontWant'), OTHER: this.$t('afterSales.reasonOther') };
      return map[code] || code;
    },
    schemeText(type) {
      if (!type) return this.$t('afterSales.waitingReview');
      const map = { RETURN_REFUND: this.$t('afterSales.schemeReturnRefund'), EXCHANGE: this.$t('afterSales.schemeExchange'), RESEND: this.$t('afterSales.schemeResend'), PARTIAL_REFUND: this.$t('afterSales.schemePartialRefund'), RELIEF: this.$t('afterSales.schemeRelief'), NO_ACTION: this.$t('afterSales.schemeNoAction') };
      return map[type] || type;
    },
    returnMethodText(method) {
      if (!method) return '-';
      const map = { COURIER_PICKUP: this.$t('afterSales.returnCourierPickup'), SELF_SEND: this.$t('afterSales.returnSelfSend') };
      return map[method] || method;
    },
    centsToAmount(cents) {
      if (!cents) return '0.00';
      return (cents / 100).toFixed(2);
    },
    goBack() {
      this.$router.back();
    },
    goOrder() {
      if (this.detail.orderId) this.$router.push(`/stall/orders/${this.detail.orderId}`);
    },
    async printReceipt() {
      try {
        const res = await getRefundReceipt(this.detail.id);
        if (res.receipt) {
          this.$message.success(this.$t('afterSales.receiptReady'));
          console.log('Refund receipt:', res.receipt);
        }
      } catch (e) {
        this.$message.error(this.$t('afterSales.receiptFailed') + ': ' + (e.message || e));
      }
    }
  }
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #2d6a4f, #52b788);
  color: #fff;
  padding: calc(16px + env(safe-area-inset-top, 0px));
}
.back-btn { font-size: 12px; opacity: 0.8; cursor: pointer; margin-bottom: 4px; }
.back-btn:hover { opacity: 1; }
.page-header h1 { font-size: 17px; }
.page-header .sub { font-size: 12px; opacity: 0.8; }

.content { max-width: 420px; margin: 0 auto; padding-bottom: 60px; background: #f5f5f5; min-height: calc(100vh - 80px); }

.module { margin: 8px; background: #fff; border-radius: 8px; overflow: hidden; }
.module-header { display: flex; align-items: center; padding: 12px 12px 8px; gap: 6px; border-bottom: 1px solid #f0f0f0; }
.module-header .icon { font-size: 16px; }
.module-header .title { font-size: 14px; font-weight: 600; flex: 1; }
.module-body { padding: 12px; }

.row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 13px; }
.row .label { color: #999; flex-shrink: 0; }
.row .value { font-weight: 500; text-align: right; }
.row + .row { border-top: 1px dashed #f0f0f0; }
.row .value.mono { font-family: monospace; font-size: 12px; }
.row .value.amount { color: #ff4d4f; font-size: 15px; font-weight: 700; }

/* 进度条 */
.progress-bar { display: flex; align-items: center; overflow-x: auto; padding: 8px 0; }
.progress-step { display: flex; flex-direction: column; align-items: center; min-width: 50px; }
.progress-dot { width: 24px; height: 24px; border-radius: 50%; background: #e8e8e8; display: flex; align-items: center; justify-content: center; font-size: 11px; color: #999; }
.progress-dot.done { background: #52c41a; color: #fff; }
.progress-dot.active { background: #1890ff; color: #fff; }
.progress-label { font-size: 9px; color: #999; margin-top: 3px; text-align: center; }
.progress-label.active { color: #1890ff; }

/* 操作日志 */
.log-item { display: flex; gap: 8px; padding: 8px 0; border-bottom: 1px solid #f5f5f5; }
.log-item:last-child { border-bottom: none; }
.log-dot { width: 6px; height: 6px; border-radius: 50%; background: #457b9d; margin-top: 5px; flex-shrink: 0; }
.log-content { flex: 1; }
.log-title { font-size: 12px; font-weight: 500; }
.log-note { font-size: 11px; color: #666; margin-top: 1px; }
.log-time { font-size: 10px; color: #bbb; margin-top: 2px; }
.empty-state { text-align: center; padding: 24px 0; color: #999; }
.empty-icon { font-size: 32px; margin-bottom: 8px; }
.empty-text { font-size: 13px; }

.bottom-actions { position: sticky; bottom: 0; background: #fff; border-top: 1px solid #e8e8e8; padding: 12px; display: flex; gap: 8px; }
</style>
